import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { LauncherComponent } from 'src/app/components/desktop/launcher/launcher.component';
import { IContextMenuAction } from 'src/app/components/ux/context-menu/context-menu-action.interface';
import { ContextMenuComponent } from 'src/app/components/ux/context-menu/context-menu.component';
import { IFile } from 'src/app/interfaces/io/file.interface';
import { VfmService } from 'src/app/services/io/vfm.service';
import { WindowsManagerService } from 'src/app/services/os/windows-manager.service';
import { ShellService } from 'src/app/services/process/shell.service';
import { ContextMenuService } from 'src/app/services/ux/context-menu.service';
import { DialogService } from 'src/app/services/ux/dialog.service';
import { LoaderService } from 'src/app/services/ux/loader.service';
import { ThemeService } from 'src/app/services/ux/theme.service';
import { ExplorerManager } from '../../classes/explorer-manager';

@Component({
  selector: 'app-explorer-view',
  templateUrl: './explorer-view.component.html',
  styleUrls: ['./explorer-view.component.scss']
})
export class ExplorerViewComponent implements OnInit, OnDestroy {
  @ViewChild("folderMenu") folderMenu: ContextMenuComponent;
  @ViewChild("iconsMenu") iconsMenu: ContextMenuComponent;

  @Input() windowId: number;
  @Input() explorerManager: ExplorerManager;

  files: IFile[] = [];

  fileMenuActions: IContextMenuAction[] = [];
  currentFolderMenuActions: IContextMenuAction[] = [];

  isLoading = false;

  private selected: IFile = null;
  private selectedDate = new Date();

  private subscription: Subscription;

  constructor(
    public theme: ThemeService,
    private contextMenuSv: ContextMenuService,
    private windowSv: WindowsManagerService,
    private shell: ShellService,
    private loaderSv: LoaderService,
    private dialog: DialogService,
    private vfm: VfmService) {
  }

  ngOnInit(): void {
    this.subscription = this.explorerManager.changeEvent.subscribe(() => {
      this.showFiles();
    });
    this.showFiles();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private get windowPos() {
    return this.windowSv.getWindowPosition(this.windowId);
  }

  private get currentUnit(): string {
    const unit = this.vfm.extractUnit(this.explorerManager.cwd);
    return unit;
  }

  get isMounted() {
    const drive = this.vfm.getDrive(this.currentUnit);

    return drive.isMounted;
  }

  get canMountUnit() {
    return true;
  }

  get iconSize() {
    return { w: 120, h: 120 };
  }

  isSelected(file: IFile) {
    return this.selected && this.selected.path === file.path;
  }

  isDirectory(file: any) {
    return this.vfm.isDirectory(file);
  }

  navigate(path: string) {
    this.explorerManager.cwd = path;
  }

  private async openFolder(file: IFile) {
    if (file.security && file.security.authentication) {
      const securityCheck = await this.shell.validateSecurity(file);
      if (!securityCheck) {
        this.dialog.error("You don't have permissions to access to this folder.", "Security");
        return;
      }
    }

    if (file.path && file.path.includes(":/")) {
      this.explorerManager.navigateTo(file.path);
    }
    else {
      this.explorerManager.navigateTo("./" + file.path);
    }
  }

  manageCurrentFolderClick(ev: MouseEvent) {
    this.selected = null;
    this.selectedDate = null;
    this.closeContextMenu();
  }

  manageClick(ev: MouseEvent, launcher: LauncherComponent, file: IFile) {
    ev.preventDefault();
    ev.stopPropagation();

    this.closeContextMenu();

    const d = new Date();
    if (this.isSelected(file)) {
      if (ev.button === 0) {
        if (this.selectedDate && (d.getTime() - this.selectedDate.getTime()) <= 400) {
          if (this.isDirectory(file)) {
            this.openFolder(file);
          }
          else {
            launcher.launch(file, this.explorerManager.cwd);
          }
          this.selectedDate = d;
          return;
        }
      }
    }

    this.selected = file;
    this.selectedDate = d;
  }

  showFileContextMenu(ev: MouseEvent, launcher: LauncherComponent, file: IFile) {
    ev.preventDefault();
    ev.stopPropagation();
    this.closeContextMenu();

    if (this.isDirectory(file)) {
      this.fileMenuActions = [
        { name: "Open", action: () => { this.openFolder(file); } }
      ];
    }
    else {
      this.fileMenuActions = [
        { name: "Open", action: () => { launcher.launch(file, this.explorerManager.cwd); } }
      ];
    }

    console.log("Context", ev);

    this.contextMenuSv.show(this.iconsMenu, ev.pageX - this.windowPos.x, ev.pageY - this.windowPos.y);
  }

  showFolderContextMenu(ev: any) {
    ev.preventDefault();
    ev.stopPropagation();
    this.closeContextMenu();
    this.contextMenuSv.show(this.folderMenu, ev.pageX - this.windowPos.x, ev.pageY - this.windowPos.y);
  }

  closeContextMenu() {
    this.contextMenuSv.close();
  }

  async mountUnit() {
    const promise = this.vfm.mount(this.currentUnit);
    await this.loaderSv.loadPromise(promise, `Mounting drive ${this.currentUnit}...`);

    await this.showFiles();
  }

  private async showFiles() {
    try {
      this.isLoading = true;
      if (this.vfm.getDrive(this.currentUnit).isMounted) {
        const files = await this.vfm.list(this.explorerManager.cwd);
        this.files = files;
      }
      else {
        this.files = [];
      }
    }
    finally {
      this.isLoading = false;
    }
  }
}
